import { useEffect, useState } from 'react'
import firebase from '../../firebase/compat'
import { client } from '../Client'

/**
 * @typedef {object} PortfolioProjectValue
 * @property {string} CompagnyID - Tenant van het project
 * @property {string} Name - Naam van het project; '' als die ontbreekt
 * @property {number} Participation - Waarde uit de participatieladder
 * @property {number} Msi - Waarde uit de monetarisering van meetstandaard-effecten
 * @property {number} Materials - Maatschappelijke besparing van materialen en adviezen
 * @property {number} Total - De drie stromen bij elkaar
 */

/**
 * @typedef {object} PortfolioSocietalValue
 * @property {Array<PortfolioProjectValue>} projects - Projecten met een bekend bedrag, hoogste eerst
 * @property {Array<string>} pending - Projecten waarvoor nog niets is uitgerekend
 * @property {number} total - De som over `projects`
 * @property {number|null} computedAt - Oudste rekenmoment in ms
 * @property {Array<string>} streams - Welke stromen in dit totaal zitten
 */

/**
 * De Maatschappelijke waarde van de projecten onder dit portfolio.
 *
 * Loopt via de Cloud Function `getPortfolioSocietalValue` en niet via
 * Firestore-hooks: een portfolio mag de collecties van zijn projecten niet
 * lezen, en een geweigerde read geeft in de browser geen fout maar een lege
 * lijst — de kaart zou dan stil op nul staan.
 * AI-CONTEXT: See docs/pitfalls.md#security-console-rules-missing-collection
 *
 * `data` is `null` zolang er geen antwoord is. Dat is iets anders dan een
 * antwoord zonder projecten, en de kaart moet die twee uit elkaar houden.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {boolean} [enabled] - Sla het ophalen over als dit geen portfolio is
 * @returns {{data: PortfolioSocietalValue|null, loading: boolean, error: Error|null}}
 */
const usePortfolioSocietalValue = (enabled = true) => {

    const [data, setData] = useState(/** @type {PortfolioSocietalValue|null} */ (null))
    const [loading, setLoading] = useState(Boolean(enabled))
    const [error, setError] = useState(/** @type {Error|null} */ (null))

    useEffect(() => {
        if (!enabled) {
            setLoading(false)
            return
        }

        let active = true
        setLoading(true)
        setError(null)

        firebase.app().functions('europe-west1')
            .httpsCallable('getPortfolioSocietalValue')({ client })
            .then((result) => {
                if (!active) return
                setData(result?.data || null)
            })
            .catch((err) => {
                // Luidruchtig: zonder deze regel is een geweigerde aanroep in
                // de console niet te onderscheiden van een portfolio zonder
                // projecten.
                // eslint-disable-next-line no-console
                console.error('usePortfolioSocietalValue:', err)
                if (active) setError(err)
            })
            .finally(() => { if (active) setLoading(false) })

        return () => { active = false }
    }, [enabled])

    return { data, loading, error }
}

export default usePortfolioSocietalValue
